import axios from "axios";

// If REACT_APP_BACKEND_URL is set (e.g. a deployed backend URL), use it as-is.
// Otherwise, fall back to whatever host the app is currently being viewed from,
// on port 8001 — so the same build works from localhost, a LAN IP (phone), or
// an ngrok/tunnel host without ever having to edit .env again.
export const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.hostname}:8001`;
export const API = `${BACKEND_URL}/api`;

// Base URL for links that go OUTSIDE the app (e.g. the receipt link in the
// customer's WhatsApp message). Phones often force http -> https, so once the
// server has HTTPS set REACT_APP_PUBLIC_BASE_URL=https://cloud32.technamami.com
// in frontend/.env and rebuild. Until then it falls back to BACKEND_URL.
export const PUBLIC_BASE_URL = (process.env.REACT_APP_PUBLIC_BASE_URL || BACKEND_URL).replace(/\/+$/, "");

// If REACT_APP_PUBLIC_BASE_URL is not set, ask the backend once — it resolves
// the server's domain to its IP (http://<ip>:8001), which phones open over
// plain http. Pages call loadPublicBaseUrl() on mount, then use
// getPublicBaseUrl() synchronously inside the WhatsApp click handler.
let _publicBase = process.env.REACT_APP_PUBLIC_BASE_URL ? PUBLIC_BASE_URL : null;
let _publicBasePromise = null;

export function getPublicBaseUrl() {
  return _publicBase || BACKEND_URL;
}

export function loadPublicBaseUrl() {
  if (_publicBase) return Promise.resolve(_publicBase);
  if (!_publicBasePromise) {
    _publicBasePromise = api
      .get("/public-base-url")
      .then((r) => {
        const b = r?.data?.base_url;
        if (b) _publicBase = String(b).replace(/\/+$/, "");
        return getPublicBaseUrl();
      })
      .catch(() => {
        _publicBasePromise = null; // retry next time
        return getPublicBaseUrl();
      });
  }
  return _publicBasePromise;
}

// "/uploads/receipts/x.jpg" -> full link for the customer's WhatsApp message
export function publicLink(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${getPublicBaseUrl()}${path.startsWith("/") ? "" : "/"}${path}`;
}

const api = axios.create({ baseURL: API });

// The token lives in sessionStorage (not localStorage) on purpose — it must
// NOT survive closing the browser/PWA and reopening it later. Every fresh
// app start has to hit the login screen again, for every role.
api.interceptors.request.use((cfg) => {
  const t = sessionStorage.getItem("token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Do not throw for 409 (duplicate) — the caller handles the confirmation flow.
// On 401 (missing/expired/invalid token, or the user no longer exists), clear
// the stale token and bounce to /login instead of leaving the app stuck
// silently re-failing every poll (e.g. Dashboard's 3s interval).
//
// GET requests get one automatic retry on a network drop (no response at all —
// e.g. backend mid-restart from --reload) since retrying a read is always safe.
// Non-GET requests (POST/PATCH/DELETE) are NOT auto-retried here, since retrying
// a write that actually succeeded server-side but lost its response could create
// a duplicate. Instead we flag the error so the caller can re-check server state
// before showing the user anything.
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err?.response?.status === 401) {
      const wasLoggedIn = !!sessionStorage.getItem("token");
      sessionStorage.removeItem("token");
      if (wasLoggedIn && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(err);
    }

    const isNetworkError = !err?.response; // request never got a response back
    if (isNetworkError) {
      err.isNetworkError = true;
      const method = (err?.config?.method || "get").toLowerCase();
      if (method === "get" && !err.config.__retried) {
        err.config.__retried = true;
        await new Promise((res) => setTimeout(res, 600));
        return api(err.config);
      }
    }
    return Promise.reject(err);
  }
);

export default api;

/**
 * Normalise error into a user-facing string.
 * Supports the new envelope { success:false, error:{code,message,details} }
 * and the legacy FastAPI shape { detail: "..." } or { detail: [{msg}] }.
 */
export function formatErr(err) {
  const d = err?.response?.data;
  if (!d) return err?.isNetworkError ? "Connection dropped — please check and try again." : (err?.message || "Network error — please try again.");
  if (d?.error?.message) return d.error.message;
  if (typeof d.detail === "string") return d.detail;
  if (Array.isArray(d.detail)) return d.detail.map((e) => e.msg || JSON.stringify(e)).join(", ");
  if (d?.detail?.message) return d.detail.message;
  return err.message || "Something went wrong.";
}

/** Return the machine-readable error code from either envelope shape. */
export function errCode(err) {
  const d = err?.response?.data;
  return d?.error?.code || d?.detail?.code || `HTTP_${err?.response?.status || "UNKNOWN"}`;
}

/** Return the details object (e.g. { invoice_no, existing }) if present. */
export function errDetails(err) {
  const d = err?.response?.data;
  return d?.error?.details || d?.detail?.details || {};
}